// The phone's arrangement: home screens of widgets and app icons, the dock,
// and the launcher's order and hidden apps. Synced on the Settings record
// (`spec.phone`) so a new phone opens on the same home screens.
//
// This is pure and repairs whatever it is handed: a
// value from an older build, another device mid-edit or a hand edit must never
// leave the phone with no way home, so anything unusable becomes the default.
// An app merged into another (2026-09-24, `MERGED_INTO`) becomes that app,
// once. Other app ids are not checked here (pinned pages come and go, a
// workspace hides pages); the views skip an id they cannot resolve.

import { MERGED_INTO } from '@/lib/nav'
import { ALL } from '@/kinds/workspace/schema'

export type WidgetKind =
  | 'clock'
  | 'today'
  | 'upcoming'
  | 'inbox'
  | 'focus'
  | 'nowplaying'
  | 'prime'
  | 'wellbeing'
  | 'list'
  | 'music'
  | 'games'
  | 'dates'
  | 'jobs'
  | 'weather'
  | 'house'
  | 'lyrics'
  | 'upnext'

/** small is 2x2 cells, wide 4x2, large 4x4, on a four-column grid */
export type WidgetSize = 'small' | 'wide' | 'large'

export type HomeItem =
  | { k: string; t: 'app'; id: string }
  /** `ref` is what the widget is about, where it needs one (the list widget's list id) */
  | { k: string; t: 'widget'; kind: WidgetKind; size: WidgetSize; ref?: string }

/** The animated backdrop behind Home and the Apps screen (os/Backdrop.tsx). */
export type PhoneWallpaper = 'dusk' | 'drift' | 'aurora' | 'night' | 'tide' | 'lines' | 'haunt' | 'none'
export const WALLPAPERS: PhoneWallpaper[] = ['dusk', 'drift', 'aurora', 'night', 'tide', 'lines', 'haunt', 'none']
/** Names from before the backdrops (the desk's wallpapers), mapped to their nearest. */
const OLD_WALLPAPER: Record<string, PhoneWallpaper> = {
  orbs: 'drift',
  bokeh: 'drift',
  starfield: 'night',
  wave: 'tide',
  sky: 'dusk',
}

export interface PhoneLayout {
  pages: HomeItem[][]
  /** the three chosen slots of the tab bar; the fourth is always the launcher */
  dock: string[]
  /** the launcher's order; apps not listed follow in their natural order */
  order: string[]
  /** apps kept out of the launcher and off the home screens */
  hidden: string[]
  wallpaper: PhoneWallpaper
  /** icons tinted per app, or plain like the sidebar */
  icons: 'colour' | 'mono'
  /** app icon edge in CSS pixels, on Home and in the launcher */
  iconSize: number
}

/** The icon sizes the slider snaps to; 48 is the default. */
export const ICON_SIZES = [40, 44, 48, 52, 56, 60, 64]
export const ICON_MIN = 40
export const ICON_MAX = 64
export const ICON_DEFAULT = 48
/** How close a dragged size must come to a common one to settle on it. */
export const ICON_SNAP = 1.5

/** A dragged size: settles on a common size when close to one, else the whole pixel it is at. */
export const snapIconSize = (v: number): number => {
  const clamped = Math.min(ICON_MAX, Math.max(ICON_MIN, v))
  const near = ICON_SIZES.find((s) => Math.abs(s - clamped) <= ICON_SNAP)
  return near ?? Math.round(clamped)
}
/** Home's grid row fits the icon and its label. */
export const homeRow = (iconSize: number) => iconSize + 30
/** The launcher keeps labels readable: four across once icons are large. */
export const launcherColumns = (iconSize: number) => (iconSize >= 60 ? 4 : 5)

export const MAX_PAGES = 6
export const MAX_PER_PAGE = 24
export const DOCK_SLOTS = 3

export const WIDGET_KINDS: WidgetKind[] = [
  'clock',
  'today',
  'upcoming',
  'inbox',
  'focus',
  'nowplaying',
  'prime',
  'wellbeing',
  'list',
  'music',
  'games',
  'dates',
  'jobs',
  'weather',
  'house',
  'lyrics',
  'upnext',
]

/** Which sizes each widget draws well; the first is what it is added at. */
export const WIDGET_SIZES: Record<WidgetKind, WidgetSize[]> = {
  clock: ['wide', 'small'],
  today: ['wide', 'large'],
  upcoming: ['wide', 'large'],
  inbox: ['small', 'wide'],
  focus: ['small', 'wide'],
  nowplaying: ['small', 'wide'],
  prime: ['wide'],
  wellbeing: ['small', 'wide'],
  list: ['wide', 'small', 'large'],
  music: ['small', 'wide', 'large'],
  games: ['small', 'wide', 'large'],
  dates: ['wide', 'small', 'large'],
  jobs: ['small', 'wide'],
  weather: ['small', 'wide'],
  house: ['wide', 'large'],
  lyrics: ['wide', 'large'],
  upnext: ['wide'],
}

/** Grid cells a thing covers: [columns, rows]. */
export const span = (item: HomeItem): [number, number] =>
  item.t === 'app' ? [1, 1] : item.size === 'small' ? [2, 2] : item.size === 'wide' ? [4, 2] : [4, 4]

let counter = 0
/** A short key, unique enough within one layout. */
export const newKey = () =>
  `${Date.now().toString(36)}${(counter++).toString(36)}${Math.random().toString(36).slice(2, 5)}`

const app = (id: string, k: string): HomeItem => ({ k, t: 'app', id })
const widget = (kind: WidgetKind, size: WidgetSize, k: string): HomeItem => ({ k, t: 'widget', kind, size })

export const defaultLayout = (): PhoneLayout => ({
  pages: [
    [
      widget('today', 'wide', 'd1'),
      widget('focus', 'small', 'd2'),
      widget('nowplaying', 'small', 'd3'),
      app('upcoming', 'd4'),
      app('projects', 'd5'),
      app('pages', 'd6'),
      app('agents', 'd7'),
    ],
    [
      widget('prime', 'wide', 'd8'),
      widget('inbox', 'small', 'd9'),
      widget('wellbeing', 'small', 'd10'),
      app('people', 'd11'),
      app('dates', 'd12'),
      app('music', 'd13'),
      app('focus', 'd14'),
      app('search', 'd15'),
      app('all', 'd19'),
      app('log', 'd16'),
      app('review', 'd17'),
      app('settings', 'd18'),
    ],
  ],
  // Home first, like a phone's home button sits first; Today and Inbox either side of the plus
  dock: ['home', 'today', 'inbox'],
  order: [],
  hidden: [],
  // the hour's sky with a low sun breathing: the Horizon backdrop
  wallpaper: 'dusk',
  icons: 'colour',
  iconSize: ICON_DEFAULT,
})

/**
 * What a workspace member's Home holds: the day and the pages of the workspace
 * shared with them. No music, assistant, wellbeing, people or dates, which are
 * the owner's and which their token cannot reach.
 */
export const memberLayout = (): PhoneLayout => ({
  ...defaultLayout(),
  pages: [
    [
      widget('today', 'wide', 'm1'),
      widget('inbox', 'small', 'm2'),
      widget('clock', 'small', 'm3'),
      app('upcoming', 'm4'),
      app('projects', 'm5'),
      app('pages', 'm6'),
      app('review', 'm7'),
      app('search', 'm8'),
      app('all', 'm11'),
      app('ask', 'm10'),
      app('settings', 'm9'),
    ],
  ],
})

/** Widgets that reach what is the owner's alone: his Prime, his wellbeing, his house. An admin member's Home has none. */
export const OWNER_ONLY_WIDGETS: WidgetKind[] = ['prime', 'wellbeing', 'house']

/** The widgets an admin member can add. */
export const adminWidgets = (): WidgetKind[] => WIDGET_KINDS.filter((k) => !OWNER_ONLY_WIDGETS.includes(k))

/** Her layout without the owner's widgets (and the apps she has no page for are dropped where the nav is read). */
export const forAdmin = (l: PhoneLayout): PhoneLayout => ({
  ...l,
  pages: l.pages.map((p) => p.filter((x) => x.t !== 'widget' || !OWNER_ONLY_WIDGETS.includes(x.kind))),
})

/** What an admin member's Home starts as: the day, the inbox, and the apps she has all of (the owner's, but his agents). */
export const adminLayout = (): PhoneLayout => ({
  ...defaultLayout(),
  pages: [
    [
      widget('today', 'wide', 'a1'),
      widget('inbox', 'small', 'a2'),
      widget('clock', 'small', 'a3'),
      app('upcoming', 'a4'),
      app('projects', 'a5'),
      app('pages', 'a6'),
      app('ask', 'a7'),
    ],
    [
      widget('dates', 'wide', 'a8'),
      widget('weather', 'small', 'a9'),
      widget('focus', 'small', 'a10'),
      app('people', 'a11'),
      app('dates', 'a12'),
      app('music', 'a13'),
      app('review', 'a14'),
      app('search', 'a15'),
      app('log', 'a16'),
      app('settings', 'a17'),
    ],
  ],
})

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v: unknown, max = 120): v is string => typeof v === 'string' && v.length > 0 && v.length <= max
const strings = (v: unknown, max: number): string[] =>
  Array.isArray(v) ? [...new Set(v.filter((x): x is string => str(x)))].slice(0, max) : []
/** App ids with the merged ones mapped to the app they joined, each once. */
const apps = (v: unknown, max: number): string[] =>
  [...new Set(strings(v, 400).map((id) => MERGED_INTO[id] ?? id))].slice(0, max)
/** One icon per app on the home screens: a merge can leave two of the same. */
const oncePerApp = (pages: HomeItem[][]): HomeItem[][] => {
  const seen = new Set<string>()
  return pages.map((p) =>
    p.filter((x) => {
      if (x.t !== 'app') return true
      if (seen.has(x.id)) return false
      seen.add(x.id)
      return true
    }),
  )
}

const item = (raw: unknown, seen: Set<string>): HomeItem | null => {
  if (!isObj(raw)) return null
  let k = str(raw.k, 40) ? raw.k : newKey()
  if (seen.has(k)) k = newKey()
  seen.add(k)
  if (raw.t === 'app' && str(raw.id)) return { k, t: 'app', id: MERGED_INTO[raw.id] ?? raw.id }
  if (raw.t === 'widget' && WIDGET_KINDS.includes(raw.kind as WidgetKind)) {
    const kind = raw.kind as WidgetKind
    const size = WIDGET_SIZES[kind].includes(raw.size as WidgetSize)
      ? (raw.size as WidgetSize)
      : WIDGET_SIZES[kind][0]!
    return { k, t: 'widget', kind, size, ...(str(raw.ref, 64) ? { ref: raw.ref } : {}) }
  }
  return null
}

/**
 * Coerce whatever was stored into a usable layout. An empty page list is kept
 * as one empty page (everything removed is a wish, not a fault), and a dock
 * short of slots is filled from the default so the bar never has holes.
 */
export const sanitise = (raw: unknown): PhoneLayout => {
  const d = defaultLayout()
  if (!isObj(raw)) return d
  const seen = new Set<string>()
  const pages = Array.isArray(raw.pages)
    ? raw.pages.slice(0, MAX_PAGES).map((p) =>
        Array.isArray(p)
          ? p
              .map((x) => item(x, seen))
              .filter((x): x is HomeItem => !!x)
              .slice(0, MAX_PER_PAGE)
          : [],
      )
    : d.pages
  const dock = apps(raw.dock, DOCK_SLOTS)
  for (const fill of d.dock) if (dock.length < DOCK_SLOTS && !dock.includes(fill)) dock.push(fill)
  return {
    pages: pages.length ? oncePerApp(pages) : [[]],
    dock,
    order: apps(raw.order, 200),
    hidden: apps(raw.hidden, 200),
    wallpaper: WALLPAPERS.includes(raw.wallpaper as PhoneWallpaper)
      ? (raw.wallpaper as PhoneWallpaper)
      : (OLD_WALLPAPER[String(raw.wallpaper)] ?? d.wallpaper),
    icons: raw.icons === 'mono' ? 'mono' : 'colour',
    iconSize:
      typeof raw.iconSize === 'number' && Number.isFinite(raw.iconSize)
        ? Math.round(Math.min(ICON_MAX, Math.max(ICON_MIN, raw.iconSize)))
        : ICON_DEFAULT,
  }
}

// ---- one per suite ----------------------------------------------------------

/** The two Settings keys the arrangement lives in. */
export interface PhoneSettings {
  phone?: Record<string, unknown>
  phones?: Record<string, Record<string, unknown>>
}

/** How many suites keep their own arrangement (the Settings schema's cap on `phones`). */
export const MAX_SUITE_PHONES = 32

/**
 * What is stored for the Home under `scope`: a suite's own (`phones[slug]`), else
 * the shared `phone`, which is also what Everything always uses. Unrepaired: the
 * caller sanitises it.
 */
export const storedPhone = (s: PhoneSettings, scope: string): unknown =>
  scope === ALL ? s.phone : (s.phones?.[scope] ?? s.phone)

/**
 * The Settings patch that saves `next` as the Home under `scope`. A spec patch
 * replaces a whole top-level key, so `phones` is written whole: every other
 * suite's arrangement is carried over (past the cap, the oldest go first).
 */
export const phonePatch = (s: PhoneSettings, scope: string, next: PhoneLayout): PhoneSettings => {
  const layout = next as unknown as Record<string, unknown>
  if (scope === ALL) return { phone: layout }
  const others = Object.entries(s.phones ?? {}).filter(([slug]) => slug !== scope)
  return {
    phones: Object.fromEntries([...others.slice(-(MAX_SUITE_PHONES - 1)), [scope, layout]]),
  }
}

/**
 * A layout narrowed to the apps a suite has: icons for apps it lacks go, and the
 * dock is filled from `fill` (then the apps themselves) so the bar never has holes.
 */
export const fitLayout = (
  l: PhoneLayout,
  apps: readonly string[],
  fill: readonly string[] = [],
): PhoneLayout => {
  const has = new Set(apps)
  const dock = l.dock.filter((id) => has.has(id))
  for (const id of [...fill, ...apps])
    if (dock.length < DOCK_SLOTS && has.has(id) && !dock.includes(id)) dock.push(id)
  return {
    ...l,
    pages: l.pages.map((p) => p.filter((x) => x.t !== 'app' || has.has(x.id))),
    dock,
    order: l.order.filter((id) => has.has(id)),
    hidden: l.hidden.filter((id) => has.has(id)),
  }
}

// ---- home screens -----------------------------------------------------------

const withPages = (l: PhoneLayout, pages: HomeItem[][]): PhoneLayout => ({ ...l, pages })

/** Put something on a page (the last one by default); a full page spills onto a new one. */
export const addItem = (l: PhoneLayout, it: HomeItem, page = l.pages.length - 1): PhoneLayout => {
  const pages = l.pages.map((p) => [...p])
  let at = Math.min(Math.max(page, 0), pages.length - 1)
  if (pages[at]!.length >= MAX_PER_PAGE) {
    if (pages.length >= MAX_PAGES) return l
    pages.push([])
    at = pages.length - 1
  }
  pages[at]!.push(it)
  return withPages(l, pages)
}

export const removeItem = (l: PhoneLayout, key: string): PhoneLayout =>
  withPages(
    l,
    l.pages.map((p) => p.filter((x) => x.k !== key)),
  )

/** Move within a page: the item at `from` goes to `to`, the rest close up. */
export const moveItem = (l: PhoneLayout, page: number, from: number, to: number): PhoneLayout => {
  const p = l.pages[page]
  if (!p || from === to || from < 0 || to < 0 || from >= p.length || to >= p.length) return l
  const next = [...p]
  const [it] = next.splice(from, 1)
  next.splice(to, 0, it!)
  return withPages(
    l,
    l.pages.map((x, i) => (i === page ? next : x)),
  )
}

/** Send an item to the end of another page, making that page if it is one past the last. */
export const moveToPage = (l: PhoneLayout, key: string, page: number): PhoneLayout => {
  const it = l.pages.flat().find((x) => x.k === key)
  if (!it || page < 0 || page > l.pages.length || page >= MAX_PAGES) return l
  const pages = l.pages.map((p) => p.filter((x) => x.k !== key))
  if (page === pages.length) pages.push([])
  if (pages[page]!.length >= MAX_PER_PAGE) return l
  pages[page]!.push(it)
  return withPages(l, pages)
}

export const resizeItem = (l: PhoneLayout, key: string, size: WidgetSize): PhoneLayout =>
  withPages(
    l,
    l.pages.map((p) =>
      p.map((x) =>
        x.k === key && x.t === 'widget' && WIDGET_SIZES[x.kind].includes(size) ? { ...x, size } : x,
      ),
    ),
  )

export const addPage = (l: PhoneLayout): PhoneLayout =>
  l.pages.length >= MAX_PAGES ? l : withPages(l, [...l.pages, []])

/** Leaving edit mode: empty pages go, but there is always at least one. */
export const dropEmptyPages = (l: PhoneLayout): PhoneLayout => {
  const pages = l.pages.filter((p) => p.length)
  return withPages(l, pages.length ? pages : [[]])
}

export const onHome = (l: PhoneLayout, id: string) =>
  l.pages.some((p) => p.some((x) => x.t === 'app' && x.id === id))

/** Take every icon for an app off the home screens (widgets stay). */
export const removeApp = (l: PhoneLayout, id: string): PhoneLayout =>
  withPages(
    l,
    l.pages.map((p) => p.filter((x) => !(x.t === 'app' && x.id === id))),
  )

// ---- dock and launcher ------------------------------------------------------

export const setDock = (l: PhoneLayout, slot: number, id: string): PhoneLayout => {
  if (slot < 0 || slot >= DOCK_SLOTS) return l
  const dock = [...l.dock]
  // an app already in another slot swaps into the one it leaves
  const was = dock.indexOf(id)
  if (was >= 0) dock[was] = dock[slot]!
  dock[slot] = id
  return { ...l, dock }
}

/** Hidden apps leave the launcher and the home screens; the dock is an explicit choice and keeps them. */
export const hideApp = (l: PhoneLayout, id: string): PhoneLayout =>
  l.hidden.includes(id) ? l : { ...removeApp(l, id), hidden: [...l.hidden, id] }

export const showApp = (l: PhoneLayout, id: string): PhoneLayout => ({
  ...l,
  hidden: l.hidden.filter((x) => x !== id),
})

/** The launcher's apps in order: the saved order first, anything new after, hidden ones out. */
export const launcherOrder = (l: PhoneLayout, ids: string[]): string[] => {
  const known = new Set(ids)
  const saved = l.order.filter((id) => known.has(id))
  const rest = ids.filter((id) => !saved.includes(id))
  return [...saved, ...rest].filter((id) => !l.hidden.includes(id))
}

/** Move an app within the launcher's visible order and save the whole order. */
export const moveInLauncher = (l: PhoneLayout, ids: string[], from: number, to: number): PhoneLayout => {
  const visible = launcherOrder(l, ids)
  if (from === to || from < 0 || to < 0 || from >= visible.length || to >= visible.length) return l
  const [id] = visible.splice(from, 1)
  visible.splice(to, 0, id!)
  return { ...l, order: visible }
}
